import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Lock, Globe, Crown, Shield, UserPlus, LogOut,
  Heart, MessageCircle, Send, X, Loader2, ChevronLeft, UserMinus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Navbar } from "../components/Navbar";
import { ImageUpload } from "../components/ImageUpload";
import { toast } from "sonner";
import api from "../lib/api";

export default function Groups() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [groupPosts, setGroupPosts] = useState([]);
  const [newPost, setNewPost] = useState({ content: "", image_url: "" });
  const [newGroup, setNewGroup] = useState({ name: "", description: "", community_type: "fitness", is_private: true });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (!stored) { navigate("/auth"); return; }
    setUser(JSON.parse(stored));
  }, [navigate]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/groups?community_type=${filter}`);
      setGroups(res.data);
    } catch (err) { console.error('Error:', err.message || err); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (user) fetchGroups(); }, [user, fetchGroups]);

  const createGroup = async () => {
    if (!newGroup.name.trim()) return;
    try {
      await api.post("/groups", newGroup);
      toast.success("Group created!");
      setShowCreate(false);
      setNewGroup({ name: "", description: "", community_type: "fitness", is_private: true });
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openGroup = async (groupId) => {
    setSelectedGroup(groupId);
    try {
      const [detailRes, postsRes] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/posts`).catch(() => ({ data: [] }))
      ]);
      setGroupDetail(detailRes.data);
      setGroupPosts(postsRes.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Members only");
      else toast.error("Failed to load group");
    }
  };

  const joinGroup = async (groupId) => {
    try {
      await api.post(`/groups/${groupId}/join`);
      toast.success("Joined group!");
      fetchGroups();
      if (selectedGroup === groupId) openGroup(groupId);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const leaveGroup = async (groupId) => {
    if (!window.confirm("Leave this group?")) return;
    try {
      await api.post(`/groups/${groupId}/leave`);
      toast.success("Left group");
      setSelectedGroup(null);
      setGroupDetail(null);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const postToGroup = async () => {
    if (!newPost.content.trim()) return;
    try {
      await api.post(`/groups/${selectedGroup}/posts`, { content: newPost.content, image_url: newPost.image_url || null });
      setNewPost({ content: "", image_url: "" });
      const res = await api.get(`/groups/${selectedGroup}/posts`);
      setGroupPosts(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to post"); }
  };

  const likePost = async (postId) => {
    try {
      await api.post(`/groups/${selectedGroup}/posts/${postId}/like`);
      const res = await api.get(`/groups/${selectedGroup}/posts`);
      setGroupPosts(res.data);
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const promoteMember = async (targetId) => {
    try {
      await api.post(`/groups/${selectedGroup}/promote`, { user_id: targetId });
      toast.success("Promoted to co-admin!");
      openGroup(selectedGroup);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const demoteMember = async (targetId) => {
    try {
      await api.post(`/groups/${selectedGroup}/demote`, { user_id: targetId });
      toast.success("Demoted to member");
      openGroup(selectedGroup);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (targetId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.post(`/groups/${selectedGroup}/remove`, { user_id: targetId });
      toast.success("Member removed");
      openGroup(selectedGroup);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const imgUrl = (url) => url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  if (!user) return null;

  // Group detail view
  if (selectedGroup && groupDetail) {
    const isAdmin = groupDetail.is_admin;
    return (
      <div className="min-h-screen bg-transparent" data-testid="group-detail-view">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button onClick={() => { setSelectedGroup(null); setGroupDetail(null); }} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B6E4F] mb-6 transition-colors">
            <ChevronLeft className="h-4 w-4" /> Back to Groups
          </button>

          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="font-['Outfit'] text-2xl font-bold text-[#0F172A]">{groupDetail.name}</h1>
                  {groupDetail.is_private ? <Lock className="h-4 w-4 text-slate-400" /> : <Globe className="h-4 w-4 text-slate-400" />}
                </div>
                <p className="text-sm text-slate-500 mb-3">{groupDetail.description}</p>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {groupDetail.member_count} members</span>
                  <span className="bg-slate-100 px-2 py-0.5 rounded-full">{groupDetail.community_type}</span>
                </div>
              </div>
              {groupDetail.is_member && !groupDetail.is_owner && (
                <Button size="sm" variant="outline" onClick={() => leaveGroup(selectedGroup)} className="rounded-full text-xs" data-testid="leave-group-btn">
                  <LogOut className="h-3 w-3 mr-1" /> Leave
                </Button>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Posts Column */}
            <div className="md:col-span-2 space-y-4">
              {groupDetail.is_member && (
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <textarea
                    value={newPost.content}
                    onChange={e => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Share with the group..."
                    data-testid="group-post-input"
                    className="w-full h-24 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-[#0B6E4F] outline-none"
                  />
                  <ImageUpload onUpload={url => setNewPost({ ...newPost, image_url: url })} currentUrl={newPost.image_url} className="mt-2" />
                  <div className="flex justify-end mt-3">
                    <Button onClick={postToGroup} data-testid="group-post-submit" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 text-sm">
                      <Send className="h-3 w-3 mr-2" /> Post
                    </Button>
                  </div>
                </div>
              )}

              {groupPosts.length === 0 ? (
                <p className="text-center text-slate-400 py-12">No posts yet. Be the first!</p>
              ) : groupPosts.map(post => (
                <div key={post.post_id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`group-post-${post.post_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {post.author?.picture ? <img src={post.author.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white text-xs font-bold">{post.author?.name?.[0]}</div>}
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{post.author?.name}</p>
                      <p className="text-xs text-slate-400">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap">{post.content}</p>
                  {post.image_url && <img src={imgUrl(post.image_url)} alt="" className="w-full max-h-80 object-cover rounded-xl mb-3 border border-slate-100" />}
                  <button onClick={() => likePost(post.post_id)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-red-500 transition-colors">
                    <Heart className={`h-4 w-4 ${post.likes?.includes(user?.user_id) ? "fill-red-500 text-red-500" : ""}`} /> {post.likes?.length || 0}
                  </button>
                </div>
              ))}
            </div>

            {/* Members Sidebar */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm h-fit" data-testid="group-members">
              <h3 className="font-semibold text-[#0F172A] mb-4 flex items-center gap-2"><Users className="h-4 w-4" /> Members</h3>
              <div className="space-y-3">
                {(groupDetail.members_list || []).map(m => (
                  <div key={m.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      {m.picture ? <img src={m.picture} alt="" className="h-7 w-7 rounded-full object-cover flex-shrink-0" /> : <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500 flex-shrink-0">{m.name?.[0]}</div>}
                      <span className="text-sm text-slate-700 truncate">{m.name}</span>
                      {m.is_owner && <Crown className="h-3 w-3 text-amber-500 flex-shrink-0" />}
                      {m.is_admin && !m.is_owner && <Shield className="h-3 w-3 text-[#0B6E4F] flex-shrink-0" />}
                    </div>
                    {isAdmin && !m.is_owner && m.user_id !== user?.user_id && (
                      <div className="flex gap-1">
                        {m.is_admin ? (
                          <button onClick={() => demoteMember(m.user_id)} title="Demote" className="p-1 text-slate-400 hover:text-amber-500"><Shield className="h-3 w-3" /></button>
                        ) : (
                          <button onClick={() => promoteMember(m.user_id)} title="Promote to co-admin" className="p-1 text-slate-400 hover:text-[#0B6E4F]"><UserPlus className="h-3 w-3" /></button>
                        )}
                        <button onClick={() => removeMember(m.user_id)} title="Remove" className="p-1 text-slate-400 hover:text-red-500"><UserMinus className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Groups listing
  return (
    <div className="min-h-screen bg-transparent" data-testid="groups-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-bold text-[#0F172A]">Private Groups</h1>
            <p className="text-sm text-slate-500 mt-1">Join or create groups to connect with like-minded people</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-group-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-5">
            <Plus className="h-4 w-4 mr-2" /> New Group
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {["all", "fitness", "health"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === f ? "bg-[#0B6E4F] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl mx-4" data-testid="create-group-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-['Outfit'] text-xl font-bold text-[#0F172A]">Create Group</h3>
                <button onClick={() => setShowCreate(false)}><X className="h-5 w-5 text-slate-400" /></button>
              </div>
              <div className="space-y-4">
                <Input value={newGroup.name} onChange={e => setNewGroup({...newGroup, name: e.target.value})} placeholder="Group name" data-testid="group-name-input" className="h-11 rounded-xl bg-slate-50" />
                <textarea value={newGroup.description} onChange={e => setNewGroup({...newGroup, description: e.target.value})} placeholder="Description" className="w-full h-20 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm resize-none" data-testid="group-desc-input" />
                <div className="flex gap-3">
                  <select value={newGroup.community_type} onChange={e => setNewGroup({...newGroup, community_type: e.target.value})} className="flex-1 h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm">
                    <option value="fitness">Fitness</option>
                    <option value="health">Health</option>
                  </select>
                  <button onClick={() => setNewGroup({...newGroup, is_private: !newGroup.is_private})}
                    className={`flex items-center gap-2 px-4 h-11 rounded-xl text-sm font-medium border transition-all ${newGroup.is_private ? "bg-slate-50 border-slate-200 text-slate-600" : "bg-[#F2F9F6] border-[#0B6E4F] text-[#0B6E4F]"}`}>
                    {newGroup.is_private ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                    {newGroup.is_private ? "Private" : "Public"}
                  </button>
                </div>
                <Button onClick={createGroup} className="w-full bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full" data-testid="create-group-submit">Create Group</Button>
              </div>
            </div>
          </div>
        )}

        {/* Groups Grid */}
        {loading ? <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div> :
          groups.length === 0 ? <p className="text-center text-slate-400 py-16">No groups yet. Create the first one!</p> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map(g => (
              <div key={g.group_id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => g.is_member ? openGroup(g.group_id) : null} data-testid={`group-card-${g.group_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${g.community_type === "fitness" ? "bg-emerald-50" : "bg-blue-50"}`}>
                    <Users className={`h-5 w-5 ${g.community_type === "fitness" ? "text-emerald-600" : "text-blue-600"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm text-[#0F172A] truncate">{g.name}</h3>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      {g.is_private ? <Lock className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                      {g.member_count} members
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-4 line-clamp-2">{g.description}</p>
                {g.is_member ? (
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openGroup(g.group_id); }} className="w-full rounded-full text-xs border-[#0B6E4F] text-[#0B6E4F]" data-testid={`open-group-${g.group_id}`}>
                    Open Group
                  </Button>
                ) : (
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); joinGroup(g.group_id); }} className="w-full rounded-full text-xs bg-[#0B6E4F] hover:bg-[#095A40] text-white" data-testid={`join-group-${g.group_id}`}>
                    <UserPlus className="h-3 w-3 mr-1" /> Join
                  </Button>
                )}
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}
